import { useEffect, useRef } from "react";
import L from "leaflet";
import { AIRPORTS } from "../data/airports.js";

// Emergency transponder codes: 7500 hijack, 7600 radio failure, 7700 general.
const EMERGENCY_SQUAWKS = new Set(["7500", "7600", "7700"]);
export const isEmergency = (ac) => EMERGENCY_SQUAWKS.has(String(ac?.squawk || "").trim());

// A single SVG plane, rotated by heading. Uses currentColor so CSS controls it.
function planeIcon(heading, selected, search, emergency) {
  const cls = [
    "plane",
    selected ? "selected" : "",
    search ? "match" : "",
    emergency ? "emergency" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return L.divIcon({
    className: "plane-wrapper",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `<div class="${cls}" style="transform:rotate(${heading || 0}deg)">
      <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
        <path d="M12 2 L14 10 L22 14 L22 16 L14 13.5 L13.5 20 L16 21.5 L16 22 L12 21 L8 22 L8 21.5 L10.5 20 L10 13.5 L2 16 L2 14 L10 10 Z"/>
      </svg></div>`,
  });
}

const MAP_KEYS = { dark: "🌑 Dark", satellite: "🛰️ Satellite", light: "🗺️ Streets" };

// A tower marker for airports that carry live ATC feeds. Kept visually distinct
// from planes so it reads as "a place you can tune into", not an aircraft.
function airportIcon() {
  return L.divIcon({
    className: "airport-wrapper",
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    html: `<div class="airport-marker" title="Live ATC">
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path d="M12 2a1 1 0 0 1 1 1v1.06a8 8 0 0 1 0 15.88V21a1 1 0 0 1-2 0v-1.06a8 8 0 0 1 0-15.88V3a1 1 0 0 1 1-1zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 3a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/>
      </svg></div>`,
  });
}

export default function MapView({ aircraft, selected, searchMatch, mapStyle = "dark", focusTarget, onSelect, onSelectAirport, onBoundsChange }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef(new Map());
  const trailsRef = useRef(null);
  const layersRef = useRef(null);
  const activeLayerRef = useRef(null);
  const boundsCbRef = useRef(onBoundsChange);
  boundsCbRef.current = onBoundsChange;
  const airportCbRef = useRef(onSelectAirport);
  airportCbRef.current = onSelectAirport;

  // Initialise the map exactly once.
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [51.47, -0.4569], // London Heathrow — a busy default
      zoom: 9,
      zoomControl: true,
      attributionControl: true,
    });

    // Base layers — a switcher lets you flip between a calm dark map, real
    // satellite imagery, and standard streets. All free / no API key.
    const osmAttr =
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
    const darkLayer = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      { attribution: `${osmAttr} &copy; <a href="https://carto.com/attributions">CARTO</a>`, maxZoom: 20 }
    );
    const satelliteLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics", maxZoom: 20 }
    );
    const streetsLayer = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      { attribution: `${osmAttr} &copy; <a href="https://carto.com/attributions">CARTO</a>`, maxZoom: 20 }
    );

    layersRef.current = { dark: darkLayer, satellite: satelliteLayer, light: streetsLayer };

    // Start on the layer that matches the current theme.
    const initial = layersRef.current[mapStyle] || darkLayer;
    initial.addTo(map);
    activeLayerRef.current = initial;

    L.control
      .layers(
        { "🌑 Dark": darkLayer, "🛰️ Satellite": satelliteLayer, "🗺️ Streets": streetsLayer },
        {},
        { position: "topright", collapsed: true }
      )
      .addTo(map);

    const emitBounds = () => {
      const b = map.getBounds();
      boundsCbRef.current?.({
        lamin: b.getSouth().toFixed(4),
        lomin: b.getWest().toFixed(4),
        lamax: b.getNorth().toFixed(4),
        lomax: b.getEast().toFixed(4),
      });
    };
    map.on("moveend", emitBounds);

    // Airports with live ATC feeds — a static layer, added once. Clicking one
    // opens the airport panel where you can listen to the tower.
    const airportLayer = L.layerGroup();
    for (const ap of AIRPORTS) {
      L.marker([ap.lat, ap.lon], {
        icon: airportIcon(),
        keyboard: true,
        title: `${ap.name} — live ATC`,
        alt: `Airport ${ap.name}`,
        zIndexOffset: -1000, // sit under planes so aircraft stay clickable
      })
        .on("click", () => airportCbRef.current?.(ap))
        .addTo(airportLayer);
    }
    airportLayer.addTo(map);

    // Layer for projected-path lines (drawn only when looking into the future).
    trailsRef.current = L.layerGroup().addTo(map);

    mapRef.current = map;

    // Centre on the user's location if they allow it. The guard prevents acting
    // on a map that was already torn down (e.g. React StrictMode double-mount, or
    // the user navigating away) before this async callback resolves.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (mapRef.current !== map) return;
          map.setView([pos.coords.latitude, pos.coords.longitude], 9);
        },
        () => {
          if (mapRef.current === map) emitBounds();
        },
        { timeout: 5000 }
      );
    }
    emitBounds();

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Fly to a search result / focus target. Guarded so a stale/removed map or an
  // un-laid-out pane can't throw inside Leaflet's animated pan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusTarget || focusTarget.lat == null) return;
    try {
      map.setView([focusTarget.lat, focusTarget.lon], focusTarget.zoom || map.getZoom(), {
        animate: false,
      });
    } catch {
      /* map not ready — ignore */
    }
  }, [focusTarget]);

  // Swap the base map layer when the theme's preferred style changes.
  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    const next = layers[mapStyle] || layers.dark;
    if (next === activeLayerRef.current) return;
    if (activeLayerRef.current) map.removeLayer(activeLayerRef.current);
    next.addTo(map);
    activeLayerRef.current = next;
  }, [mapStyle]);

  // Sync markers whenever the aircraft list or selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = markersRef.current;
    const trails = trailsRef.current;
    const seen = new Set();

    // Rebuild projected-path lines from scratch each pass (cheap; positions move
    // every refresh anyway). Empty when we're looking at "now".
    trails?.clearLayers();

    for (const ac of aircraft) {
      seen.add(ac.icao24);
      const isSel = ac.icao24 === selected;
      const isMatch = searchMatch && searchMatch.has(ac.icao24);
      const pos = [ac.latitude, ac.longitude];

      let m = markers.get(ac.icao24);
      const icon = planeIcon(ac.heading, isSel, isMatch, isEmergency(ac));
      if (!m) {
        m = L.marker(pos, {
          icon,
          keyboard: true,
          title: ac.callsign || ac.icao24,
          alt: `Aircraft ${ac.callsign || ac.icao24}`,
        });
        m.on("click", () => onSelect(ac.icao24));
        m.addTo(map);
        markers.set(ac.icao24, m);
      } else {
        m.setLatLng(pos);
        m.setIcon(icon);
      }
    }

    // Remove markers for aircraft that are no longer present.
    for (const [id, m] of markers) {
      if (!seen.has(id)) {
        map.removeLayer(m);
        markers.delete(id);
      }
    }
  }, [aircraft, selected, searchMatch, onSelect]);

  // Pan to the selected aircraft.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected) return;
    const ac = aircraft.find((a) => a.icao24 === selected);
    if (!ac) return;
    try {
      map.panTo([ac.latitude, ac.longitude], { animate: false });
    } catch {
      /* map not ready — ignore */
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="map" role="application" aria-label="Live aircraft map" />;
}
